import { sortAlpha } from "widgets/utils";

import type { Project, UserGroup } from "../api/graphql.ts";
import { CAPABILITY } from "../configuration/settings.ts";

export type MinimalProject = Partial<Project> &
  Pick<Project, "id" | "name" | "color" | "description" | "isPrivateWorkspace" | "capabilities">;
export type MinimalUserGroup<P extends MinimalProject> = Partial<UserGroup> & { accessibleProjects: P[] };
export type GroupsWithProjects<P extends MinimalProject> = Record<string, P[]>;

export function isProjectPrivateSavingAllowed<P extends Pick<MinimalProject, "isPrivateWorkspace" | "capabilities">>(
  project: P,
): boolean {
  // private saving is always allowed (actually it's mandatory) in private workspace
  // @ts-expect-error graphql schema is defined a Map, which is weird
  return project.isPrivateWorkspace || project.capabilities?.[CAPABILITY.PRIVATE_SAVING]?.enabled === true;
}

export function isProjectCommentingAllowed(project: Pick<Project, "capabilities">): boolean {
  // @ts-expect-error graphql schema is defined a Map, which is weird
  return project.capabilities?.[CAPABILITY.PROJECT_COMMENTS]?.enabled === true;
}

const isMinimalProject = <P extends MinimalProject>(project: P | null | undefined): project is P => project != null;

export const extractPrivateGroups = <P extends MinimalProject>(
  groups: GroupsWithProjects<P> | null,
): [GroupsWithProjects<P> | null, GroupsWithProjects<P> | null] => {
  if (!groups) return [null, null];

  const privateGroups: GroupsWithProjects<P> = {};
  const nonPrivateGroups: GroupsWithProjects<P> = {};

  for (const [groupName, projects] of Object.entries(groups)) {
    const validProjects = projects.filter(isMinimalProject);
    const hasPrivateProject = validProjects.some((project) => project.isPrivateWorkspace === true);

    if (hasPrivateProject) {
      privateGroups[groupName] = validProjects;
    } else {
      nonPrivateGroups[groupName] = validProjects;
    }
  }

  return [
    Object.keys(privateGroups).length > 0 ? privateGroups : null,
    Object.keys(nonPrivateGroups).length > 0 ? nonPrivateGroups : null,
  ];
};

export const splitProjectsAndFolders = <P extends MinimalProject>(projects: (P | null | undefined)[]): P[] => {
  return projects.filter(isMinimalProject).toSorted((a, b) => {
    const aHasColor = Boolean(a.color);
    const bHasColor = Boolean(b.color);

    if (aHasColor === bHasColor) return 0;
    return aHasColor ? -1 : 1;
  });
};

export const getProjectsByUserGroups = <P extends MinimalProject>(
  userGroups: readonly (MinimalUserGroup<P> | null | undefined)[] | null | undefined = [],
): GroupsWithProjects<P> | null => {
  if (!userGroups?.length) return null;

  const groupsByName = Object.groupBy(userGroups.filter(Boolean), (group) => group?.name ?? "Unknown");

  const filteredGroups = Object.fromEntries(
    Object.entries(groupsByName)
      .map(([groupName, groups]) => {
        const projects = (groups?.[0]?.accessibleProjects ?? []) as P[];
        const sortedProjects = projects
          .filter(isMinimalProject)
          .toSorted((a, b) => sortAlpha(a.name ?? "", b.name ?? ""));

        return [groupName, splitProjectsAndFolders(sortedProjects)] as const;
      })
      .filter(([, projects]) => projects.length > 0),
  );

  return Object.keys(filteredGroups).length > 0 ? filteredGroups : null;
};

export const extractAllProjectsFromGroups = <P extends MinimalProject, G extends MinimalUserGroup<P>>(
  groups?: readonly G[] | null,
): P[] | null => {
  if (!groups?.length) return null;

  const allProjects = groups
    .flatMap((group) => group.accessibleProjects)
    .filter(isMinimalProject)
    .sort((a, b) => sortAlpha(a.name ?? "", b.name ?? ""));

  return allProjects.length > 0 ? allProjects : null;
};
